import React, { useContext, useEffect } from "react";
import { Paper, Radio } from "@mui/material";

import { Node } from "ui";

import { selectLayerByVersionAndLayerKey, selectVersionConfig } from "fond/api";
import { LayerConfig, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { useAppSelector } from "fond/utils/hooks";

import { InferredIcon } from "../Legend";
import { MapContext } from "../MapProvider";

import { Container, Label, Row } from "./LayerSelection.styles";

export interface IButtonProps {
  title?: string;
  icon?: React.ReactNode;
  mode?: string;
}

interface LayerNode extends Node {
  isSublayer?: boolean;
}

const LayerSelection: React.FC = () => {
  const { drawControl, map, isDrawing } = useContext(MapContext);
  const versionId = useAppSelector((state) => state.project.versionId);
  const config = useAppSelector((state) => selectVersionConfig(state, versionId));
  const [selectedLayer, setSelectedLayer] = React.useState<string | undefined>(drawControl.current?.options?.editingLayerId);

  /**
   * Generates a list of editable layers based on the drawing control's option.editableLayers and the project's version config.
   * Each layer is represented as a node with a value, label, and icon for use in the checkbox tree.
   * When ShowSublayers is true, sublayers are also included in the list.
   */
  const editableLayers = useAppSelector((state) => {
    const layerConfigs: LayerNode[] = [];
    const layerOptions = drawControl.current?.options?.editableLayers;

    if (!layerOptions || !config) return layerConfigs;

    layerOptions.forEach((layer) => {
      const layerConfigID = selectLayerByVersionAndLayerKey(state, { versionId, layerId: layer.ID })?.Configuration.ID;
      const layerConfig = layerConfigID ? (config.Data.entities[layerConfigID] as LayerConfig) : undefined;
      if (layerConfig) {
        layerConfigs.push({
          value: layer.ID,
          label: layerConfig?.Label,
          icon: <InferredIcon entity={layerConfig} config={config} />,
          isSublayer: false,
        });

        // If ShowSublayers is true, add sublayers to the list
        if (layer.ShowSublayers && layerConfig.Children && layerConfig.Children.length > 0) {
          layerConfig.Children.forEach((sublayerId) => {
            const sublayerConfig = config.Data.entities[sublayerId] as SublayerConfig;
            if (sublayerConfig && sublayerConfig.Type === "SUBLAYER") {
              layerConfigs.push({
                value: sublayerId,
                label: sublayerConfig.Label,
                icon: <InferredIcon entity={sublayerConfig} config={config} />,
                isSublayer: true,
              });
            }
          });
        }
      }
    });

    return layerConfigs;
  });

  useEffect(() => {
    setSelectedLayer(drawControl.current?.options?.editingLayerId);
  }, [drawControl, drawControl.current?.options?.editingLayerId, map]);

  /**
   * Handles the event when a layer is selected in the radio button group.
   * Fires a custom event that other components (like MultiLayerEditHandler) will handle.
   * @param id - The ID of the layer or sublayer that has been selected.
   */
  const handleOnSelect = (id: string) => {
    if (drawControl.current && drawControl.current.options) {
      // Check if this is a sublayer by looking it up in the config
      const sublayerConfig = config?.Data.entities[id] as SublayerConfig | undefined;
      const isSublayer = sublayerConfig?.Type === "SUBLAYER";

      // For sublayers, use the sublayer's Key (which is copied from parent) as the parentLayerId
      const parentLayerId = isSublayer && sublayerConfig ? sublayerConfig.Key : id;

      // Find the editing layer using the parent layer ID
      const editingLayer = drawControl.current.options.editableLayers?.find((layer) => layer.ID === parentLayerId);

      setSelectedLayer(id);

      // Fire a custom event to notify other components (MultiLayerEditHandler will handle the logic)
      map?.fire("draw.layerchange", {
        layerId: id,
        parentLayerId: isSublayer ? parentLayerId : undefined,
        geometryType: editingLayer?.GeometryType,
        editingLayer,
        sublayerFilter: isSublayer && sublayerConfig?.FilterConfiguration?.Mapbox ? sublayerConfig.FilterConfiguration.Mapbox : undefined,
      });
    }
  };

  if (!isDrawing || !drawControl.current?.options?.editableLayers) return null;

  // If there's only one layer, no need to show selection UI
  if (editableLayers.length <= 1) return null;

  return (
    <Container>
      <Paper sx={{ p: 1 }} elevation={3}>
        {editableLayers.map(({ label, value, icon, isSublayer }) => (
          <Row key={value} sx={{ pl: isSublayer ? 2 : 0 }}>
            <Radio id={value} checked={selectedLayer === value} onChange={() => handleOnSelect(value)} size="small" sx={{ p: 0.5 }} />
            <Label htmlFor={value}>
              {icon}
              {label}
            </Label>
          </Row>
        ))}
      </Paper>
    </Container>
  );
};

export default LayerSelection;
